import type { AuthProvider } from "@refinedev/core";

import { setMustChangePassword } from "./auth/mustChangePassword";
import { markeerVerseAanmelding } from "./auth/verseAanmelding";
import { clearAccessToken, getAccessToken, setAccessToken } from "./auth/tokenStore";
import { consumePkceVerifier, exchangeCodeForToken, readOidcConfig } from "./auth/pkce";

// jve-login: Refine AuthProvider over the in-memory token store. `login` posts the operator's
// username/password to the API's /api/auth/login endpoint, which authenticates against the docasoft
// `user_account` table (Spring Security) and returns a JWT the resource server trusts; the token is
// held in memory only (never web storage). The OIDC/PKCE redirect plumbing below
// (handleRedirectCallback) stays in place but dormant — the chosen auth is username/password.

// Same base-URL convention as dataProvider: the Vite dev server proxies /api to the API.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function decodeJwtClaims(token: string): Record<string, unknown> | undefined {
  const parts = token.split(".");
  if (parts.length !== 3) {
    return undefined;
  }
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Complete the PKCE round-trip when the page loads at the redirect URI with `?code`. Returns true
 * when a token was obtained. Call once on app startup before rendering authenticated content.
 */
export async function handleRedirectCallback(): Promise<boolean> {
  const config = readOidcConfig();
  if (!config) {
    return false;
  }
  const url = new URL(globalThis.location?.href ?? "http://localhost/");
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code || !state) {
    return false;
  }
  const verifier = consumePkceVerifier(state);
  if (!verifier) {
    return false;
  }
  const tokens = await exchangeCodeForToken(config, code, verifier);
  setAccessToken(tokens.access_token);
  return true;
}

/**
 * Silently restore a session on app startup. The access token lives in memory only, so a page
 * refresh / new tab boots with none; this asks the API to re-mint one from the HttpOnly refresh
 * cookie (POST /api/auth/refresh, which also rotates the cookie). Returns true when a token was
 * obtained — call once before first render so the initial `check` sees the restored session and the
 * app renders authenticated instead of flashing the login page. A failure (no/expired cookie) simply
 * leaves the operator logged out.
 */
export async function restoreSession(): Promise<boolean> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
  } catch {
    return false;
  }
  if (!response.ok) {
    return false;
  }
  const body = (await response.json().catch(() => null)) as
    | { access_token?: string; must_change_password?: boolean }
    | null;
  if (!body?.access_token) {
    return false;
  }
  setAccessToken(body.access_token);
  // Re-derived on every restore: an admin may have flagged the account since the last page load, and
  // a change made in another tab clears it here rather than leaving a stale prompt.
  setMustChangePassword(body.must_change_password === true);
  return true;
}

/**
 * Public login-page / app config (GET /api/auth/config): whether the "remember me" option is offered
 * (driven by `fawkes.api.login.remember-enabled`) and the deployment environment name
 * (`fawkes.api.environment`: dev/test/acc/production, default "production"). Fails closed
 * (rememberEnabled=false, environment="production") when the API is unreachable or login is disabled,
 * so the checkbox stays hidden and dev-only surfaces stay off by default.
 */
export async function fetchAuthConfig(): Promise<{ rememberEnabled: boolean; environment: string }> {
  try {
    const response = await fetch(`${API_URL}/auth/config`, { headers: { Accept: "application/json" } });
    if (!response.ok) {
      return { rememberEnabled: false, environment: "production" };
    }
    const body = (await response.json().catch(() => null)) as
      | { rememberEnabled?: boolean; environment?: string }
      | null;
    return {
      rememberEnabled: body?.rememberEnabled === true,
      environment: typeof body?.environment === "string" ? body.environment : "production",
    };
  } catch {
    return { rememberEnabled: false, environment: "production" };
  }
}

/**
 * Whether the dev-only surfaces (the component/tile gallery, reachable from the left-menu "Dev" group
 * and at /gallery + /tiles) should be enabled. True under `vite dev` (build-time) or when the backing
 * API instance reports it is the deployed TEST environment; false on acc/production and on any failure.
 */
export async function devSurfacesEnabled(): Promise<boolean> {
  if (import.meta.env.DEV) return true;
  return (await fetchAuthConfig()).environment === "test";
}

export const authProvider: AuthProvider = {
  login: async (params) => {
    const { username, password, remember } = (params ?? {}) as {
      username?: string;
      password?: string;
      remember?: boolean;
    };
    if (!username || !password) {
      return {
        success: false,
        error: { name: "Login failed", message: "Enter your username and password." },
      };
    }
    let response: Response;
    try {
      response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        // `credentials: include` so the API's HttpOnly refresh cookie (Set-Cookie) is stored — it
        // backs the silent session restore on the next page load (same-origin via the Vite proxy).
        credentials: "include",
        // `remember` extends the token lifetime server-side.
        body: JSON.stringify({ username, password, remember: remember ?? false }),
      });
    } catch {
      return {
        success: false,
        error: { name: "Login failed", message: "Could not reach the server. Try again." },
      };
    }
    if (!response.ok) {
      // 401 (bad credentials/inactive) and any other failure surface the same generic message.
      return {
        success: false,
        error: { name: "Login failed", message: "Invalid username or password." },
      };
    }
    const body = (await response.json().catch(() => null)) as
      | { access_token?: string; must_change_password?: boolean }
      | null;
    if (!body?.access_token) {
      return {
        success: false,
        error: { name: "Login failed", message: "The server did not return a token." },
      };
    }
    setAccessToken(body.access_token);
    // Een verse aanmelding begint op het dashboard, niet op het scherm waar de vorige sessie
    // eindigde. Verversen blijft wél staan waar je was — zie auth/verseAanmelding.
    markeerVerseAanmelding();
    // The session is valid either way; the flag routes the shell to the forced-change screen first.
    setMustChangePassword(body.must_change_password === true);
    return { success: true, redirectTo: "/" };
  },

  logout: async () => {
    // Best-effort: revoke the refresh token + clear the cookie server-side. Never block logout on it
    // (a network error must still drop the in-memory token and return to the login page).
    try {
      await fetch(`${API_URL}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      // ignore — clearing the in-memory token below is what actually logs the operator out
    }
    clearAccessToken();
    // Drop the prompt with the session, so the next login starts from what the server reports.
    setMustChangePassword(false);
    return { success: true, redirectTo: "/login" };
  },

  check: async () => {
    if (getAccessToken()) {
      return { authenticated: true };
    }
    return { authenticated: false, redirectTo: "/login", logout: true };
  },

  onError: async (error) => {
    const status = (error as { statusCode?: number; status?: number })?.statusCode
      ?? (error as { status?: number })?.status;
    // The API refuses everything but the way out while the account holds an admin-set password. That
    // is a 403, but logging the operator out would be exactly wrong — they are signed in, they just
    // have one thing left to do. Raising the flag routes the shell to the forced-change screen, which
    // is also how a session flagged *after* login (or in another tab) lands there.
    if (status === 403 && (error as { errorCode?: string })?.errorCode === "password_change_required") {
      setMustChangePassword(true);
      return {};
    }
    if (status === 401 || status === 403) {
      return { logout: true, redirectTo: "/login", error };
    }
    return {};
  },

  getIdentity: async () => {
    const token = getAccessToken();
    if (!token) {
      return null;
    }
    const claims = decodeJwtClaims(token);
    if (!claims) {
      return { id: "operator" };
    }
    return {
      id: (claims.sub as string) ?? "operator",
      name: (claims.name as string) ?? (claims.preferred_username as string) ?? (claims.sub as string),
      email: (claims.email as string) ?? undefined,
    };
  },

  getPermissions: async () => {
    const token = getAccessToken();
    if (!token) {
      return null;
    }
    const claims = decodeJwtClaims(token);
    const authorities = claims?.authorities ?? claims?.scope ?? claims?.roles;
    if (Array.isArray(authorities)) {
      return authorities as string[];
    }
    if (typeof authorities === "string") {
      return authorities.split(" ");
    }
    return [];
  },
};
